import { Text, Section, Grid, Column } from '@acast/decibel';
import get from 'lodash.get';
import T from 'prop-types';
import React from 'react';
import categories from 'shared/helpers/categories';
import languages from 'shared/helpers/languages';
import oldCategoriesMapper from 'shared/helpers/old-categories-mapper';
import ImageField from './fields/image';
import RichTextField from './fields/richtext';
import SelectField from './fields/select';
import TagField from './fields/tag';
import TextField from './fields/text';

const SettingsGroupGeneral = ({ show, onChange, metaFunc }) => {
  const fieldProps = propName => ({
    propName,
    meta: metaFunc(propName),
    onChange: v => onChange(propName, v),
    value: get(show, propName),
  });
  const showTypeHelp = (
    <>
      <b>Episodic:</b> Episodes will be presented reverse chronologically, with the latest episode
      at the top. For long running shows or stand-alone episodes, and also supported in seasons.
      <br />
      <br />
      <b>Serial:</b> Episodes will be presented chronologically, with the first episode at the top.
      Useful for narrative or storytelling shows, or those with multiple seasons.
    </>
  );
  return (
    <Section>
      <Text variant="h3">General Settings</Text>
      <Grid>
        <Column>
          <ImageField
            showId={show && show.id}
            {...fieldProps('image.original')}
            label="Cover Image"
            help="Min. size 1400x1400 px – Max size 3000x3000 px in .png or .jpg"
            isRequired
          />
        </Column>
        <Column>
          <TextField
            {...fieldProps('name')}
            label="Name"
            placeholder="Type the name of your show"
            isRequired
          />
        </Column>
        <Column>
          <RichTextField
            {...fieldProps('description')}
            label="Description"
            placeholder="Describe your show and what it's about."
            help="Description text formatting will appear in certain Apple Podcasts views, the Acast app and web player and any podcatcher that supports HTML."
            isRequired
          />
        </Column>
        <Column width="50%">
          <TextField
            {...fieldProps('homepage')}
            label="Website"
            placeholder="E.g. http://www.facebook.com/"
            help="Add an optional link to an external website. This will appear on the Apple Podcasts show page."
          />
        </Column>
        <Column width="50%">
          <SelectField
            {...fieldProps('showType')}
            label="Type"
            help={showTypeHelp}
            options={[
              { value: 'episodic', label: 'Episodic (default)' },
              { value: 'serial', label: 'Serial' },
            ]}
          />
        </Column>
        <Column width="50%">
          <TagField
            {...fieldProps('tags')}
            label="Tags"
            placeholder="Add new tag"
            help="These are used by the search function within the Acast apps and web player."
          />
        </Column>
        <Column width="50%">
          <TagField
            {...fieldProps('categories')}
            onChange={v => {
              /*
              We'll use both old and new iTunes categories for a while
              BUT only show new categories in Create
              So on save we map new categories to corresponding old category (if any)
            */
              const oldCategories = v.map(category => oldCategoriesMapper[category]).filter(c => c);
              const mergedCategories = [...new Set([...v, ...oldCategories])];
              onChange('categories', mergedCategories);
            }}
            restrictToOptions
            options={categories}
            label="Categories"
            placeholder="Add new category"
            help="Type and select the main category of your show first, and then any additional subcategories."
          />
        </Column>
        <Column width="50%">
          <SelectField {...fieldProps('language')} label="Language" options={languages} />
        </Column>
        <Column width="50%">
          <SelectField
            {...fieldProps('rating')}
            label="Explicit Content"
            help="Select if your show contains any bad language or content that might be inappropriate for children. This will tag the whole show as 'Explicit'."
            options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]}
          />
        </Column>
      </Grid>
    </Section>
  );
};

SettingsGroupGeneral.propTypes = {
  show: T.object,
  onChange: T.func,
  metaFunc: T.func,
};

export default SettingsGroupGeneral;
